import { InboundDataExtractionItem, InboundFile, InboundQueueRow, RotationMap, ServicingUser, StoredPolicyData } from "./types";
import React, { createContext, useRef, useState } from "react";

import { DropdownItemProps } from "semantic-ui-react";
import { HighlightText } from "../../components/PDFViewer";
import { PDFDocumentProxy } from "pdfjs-dist";
import { notImplemented } from "../../services/Library";

export interface InboundContextProps {
    currentFileIndex: number;
    inboundManagers: ServicingUser[] | undefined;
    servicingUserOptions: DropdownItemProps[] | undefined;
    servicingUsers: ServicingUser[] | undefined;
    setCurrentFileIndex: React.Dispatch<React.SetStateAction<number>>;
    setInboundManagers: (data: ServicingUser[]) => void;
    setServicingUserOptions: (data: DropdownItemProps[]) => void;
    setServicingUsers: (data: ServicingUser[]) => void;
    setStoredPolicyData: React.Dispatch<React.SetStateAction<StoredPolicyData>>;
    storedPolicyData: StoredPolicyData;
    // queue grid
    queueGridData?: InboundQueueRow[];
    setQueueGridData: React.Dispatch<React.SetStateAction<InboundQueueRow[] | undefined>>;
    pdfCache: React.MutableRefObject<{ [key: string]: PDFDocumentProxy }>;
    // validation
    validationFiles: InboundFile[];
    setValidationFiles: React.Dispatch<React.SetStateAction<InboundFile[]>>;
    originalValidationFiles: InboundFile[];
    setOriginalValidationFiles: React.Dispatch<React.SetStateAction<InboundFile[]>>;
    pdfViewerPage: number;
    setPdfViewerPage: React.Dispatch<React.SetStateAction<number>>;
    haveAnyPageIndicesChanged: boolean;
    areThereNewFiles: boolean;
    dataExtraction: InboundDataExtractionItem[];
    setDataExtraction: React.Dispatch<React.SetStateAction<InboundDataExtractionItem[]>>;
    classificationOptions: DropdownItemProps[] | undefined;
    setClassificationOptions: React.Dispatch<React.SetStateAction<DropdownItemProps[] | undefined>>;
    highlighting: HighlightText[];
    setHighlighting: React.Dispatch<React.SetStateAction<HighlightText[]>>;
    rotationCache: React.MutableRefObject<RotationMap>;
}

export const InboundContext = createContext<InboundContextProps>({
    currentFileIndex: 0,
    inboundManagers: [],
    servicingUserOptions: [],
    servicingUsers: [],
    setCurrentFileIndex: notImplemented,
    setInboundManagers: notImplemented,
    setServicingUserOptions: notImplemented,
    setServicingUsers: notImplemented,
    setStoredPolicyData: notImplemented,
    storedPolicyData: {},
    // queue grid
    queueGridData: [],
    setQueueGridData: notImplemented,
    pdfCache: { current: {} },
    // validation
    validationFiles: [],
    setValidationFiles: notImplemented,
    originalValidationFiles: [],
    setOriginalValidationFiles: notImplemented,
    pdfViewerPage: 1,
    setPdfViewerPage: notImplemented,
    haveAnyPageIndicesChanged: false,
    areThereNewFiles: false,
    dataExtraction: [],
    setDataExtraction: notImplemented,
    classificationOptions: [],
    setClassificationOptions: notImplemented,
    highlighting: [],
    setHighlighting: notImplemented,
    rotationCache: { current: {} }
});

const InboundContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
    const [servicingUserOptions, setServicingUserOptions] = useState<DropdownItemProps[]>();
    const [servicingUsers, setServicingUsers] = useState<ServicingUser[]>();
    const [inboundManagers, setInboundManagers] = useState<ServicingUser[]>([]);
    const [storedPolicyData, setStoredPolicyData] = useState<StoredPolicyData>({});

    const [queueGridData, setQueueGridData] = useState<InboundQueueRow[]>();
    const [validationFiles, setValidationFiles] = useState<InboundFile[]>([]);
    const [originalValidationFiles, setOriginalValidationFiles] = useState<InboundFile[]>([]);
    const [pdfViewerPage, setPdfViewerPage] = useState<number>(1);
    const [dataExtraction, setDataExtraction] = useState<InboundDataExtractionItem[]>([]);
    const [classificationOptions, setClassificationOptions] = useState<DropdownItemProps[] | undefined>(undefined);
    const [highlighting, setHighlighting] = useState<HighlightText[]>([]);

    // keep cache of buffers for each file
    const pdfCache = useRef<{ [key: string]: PDFDocumentProxy }>({});

    const rotationCache = useRef<RotationMap>({});

    const haveAnyPageIndicesChanged = originalValidationFiles.some(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ PageIndices: originalPageIndices, FileId: originalFileId, ...originalRest }) => {
            // excludes page indices
            const currentFile = validationFiles.find((f) => f.FileId === originalFileId);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { PageIndices: currentPageIndices, FileId, ...currentRest } = currentFile || {};
            return JSON.stringify(originalPageIndices) !== JSON.stringify(currentPageIndices);
        }
    );
    const areThereNewFiles = validationFiles.some((f) => !f.isDeletedFile && !originalValidationFiles.some((of) => of.FileId === f.FileId));

    return (
        <InboundContext.Provider
            value={{
                currentFileIndex,
                inboundManagers,
                servicingUserOptions,
                servicingUsers,
                setCurrentFileIndex,
                setInboundManagers,
                setServicingUserOptions,
                setServicingUsers,
                setStoredPolicyData,
                storedPolicyData,
                // queue grid
                queueGridData,
                setQueueGridData,
                pdfCache,
                // validation
                validationFiles,
                setValidationFiles,
                originalValidationFiles,
                setOriginalValidationFiles,
                pdfViewerPage,
                setPdfViewerPage,
                haveAnyPageIndicesChanged,
                areThereNewFiles,
                dataExtraction,
                setDataExtraction,
                classificationOptions,
                setClassificationOptions,
                highlighting,
                setHighlighting,
                rotationCache
            }}
        >
            {children}
        </InboundContext.Provider>
    );
};

export default InboundContextProvider;
